import React, { useEffect, useContext, useState, useRef } from 'react';
import { FormattedMessage, useIntl, IntlContext } from 'react-intl';
import { currentLang, currentLangParam, currentLongLang, getHealthCheck, bytesToNearestUnitStr } from '../../components/components/utils';
import AppContext from '../../components/AppContext';
import { API, Auth } from 'aws-amplify';
import MagnifiquePopup from '../../components/MagnifiquePopup.js';
import WetLikeTable from './components/WetLikeTable.js';
import Spinner from '../../components/Spinner';
import PopupLink from '../../templates/PopupLink.js';

function Exhibits({ navigation }) {
	const { messages } = useContext(IntlContext);
	const appContext = useContext(AppContext);
	const intl = useIntl();
	const batchSizeLimit = 2000000000;  //2GB

	const labels = {
		"http.networkError": useIntl().formatMessage({ id: "http.networkError" }, { p: (chunks) => <p>{chunks}</p>, a: (chunks) => <a href={"https://www.cbsa-asfc.gc.ca/contact/bis-sif-" + currentLongLang() + ".html"} className="App-link" target="_blank" rel="noreferrer">{chunks}</a> })
		,"home.details.fileProcessingIsBeingSlowCheckBackLater": useIntl().formatMessage({ id: "home.details.fileProcessingIsBeingSlowCheckBackLater" }, { p: (chunks) => <p>{chunks}</p>, a: (chunks) => <a href={"https://www.cbsa-asfc.gc.ca/contact/bis-sif-" + currentLongLang() + ".html"} className="App-link" target="_blank" rel="noreferrer">{chunks}</a> })
		,"http.networkErrorFileServer" : <FormattedMessage id="http.networkErrorFileServer" values={{p: (chunks) => <p>{chunks}</p>, a: (chunks) => <a href={"https://www.cbsa-asfc.gc.ca/contact/bis-sif-"+currentLongLang()+".html"} className="App-link" target="_blank" rel="noreferrer">{chunks}</a>}}/>
		,"ace.exhibit.downloading-large-or-multiple-files.notification" : <FormattedMessage id="ace.exhibit.downloading-large-or-multiple-files.notification" values={{p: (chunks) => <p>{chunks}</p>, batchSizeLimit: bytesToNearestUnitStr(batchSizeLimit, messages), a: (chunks) => <a href={"https://www.cbsa-asfc.gc.ca/sima-lmsi/ace-dedac/help-aide-"+currentLongLang()+".html"} className="App-link" target="_blank" rel="noreferrer">{chunks}</a>, newWindowIcon: <span className="glyphicon glyphicon-new-window"></span>}}/>
	};

	const [displayPopup, setDisplayPopup] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState();
	const [downloadUrls, setDownloadUrls] = useState({});
	const [exhibitList, setExhibitList] = useState([]);
	const [cancelCallbackLatch, setCancelCallbackLatch] = useState(false);
	const [urlParams] = useState(new URLSearchParams(document.location.search));
	const [proceedingIdentifier, setProceedingIdentifier] = useState(navigation?.state?.params?.proceedingId ?? urlParams.get('proceedingId')?.replaceAll("+", " "));
	const [isAllSelected, setIsAllSelected] = useState(false);
	const [selectedRows, setSelectedRows] = useState({});
	const [prevSelectedRows, setPrevSelectedRows] = useState({});
	const buttonRef = useRef(null);
	const [showSpinner, setShowSpinner] = React.useState(false);
	const downloadsInProgressRef = useRef(0);

	useEffect(() => {
		if (isLoggedIn === false) {
			setTimeout(() => appContext.setFlashNotice({ duration: -1, messages: [messages["ace.exhibit.banner.login"]] }), 0);
		} else {
			appContext.setFlashNotice({ duration: -1, messages: [labels["ace.exhibit.downloading-large-or-multiple-files.notification"]] });
		}
	}, [isLoggedIn]);

	useEffect(() => {
		async function fetchData() {
			appContext.setDynamicPageTitle(<FormattedMessage id="ace.exhibit.page.title" values={{ proceedingIdentifier: !!proceedingIdentifier ? <><FormattedMessage id="wet.colon"/> {proceedingIdentifier}</> : <></> }} />);
			setShowSpinner(true);
			Promise.all([
				loadExhibitList()
				, getHealthCheck(appContext, labels["http.networkErrorFileServer"])
			]).catch(error => {
				if (window.env.environment === "local") {
					console.log({ error });
				}
			}).finally(() => {
				setShowSpinner(false);
			})
		}

		fetchData();
	}, []);

	async function loadExhibitList() {
		let api = 'anon-exhibits-api';
		let apiURL = 'exhibits/anon';
		try {
			await Auth.currentSession();
			api = 'backend-api';
			apiURL = 'exhibits';
			setIsLoggedIn(true);
			appContext.setExistingUser(true);
		} catch {
			setIsLoggedIn(false);
		}

		return API.get(api, apiURL, {
			queryStringParameters: { lang: currentLang(), proceedingId: proceedingIdentifier }
		}).then(response => {
			const sortedList = sortAsc(response, 'ace_name');
			setExhibitList(sortedList);
			if (sortedList.length > 0) {
				//appContext.setDynamicPageTitle(<FormattedMessage id="ace.exhibit.page.title" values={{proceedingIdentifier: !!response[0].ace_ProceedingIdentifier ? <><FormattedMessage id="wet.colon"/> {response[0].ace_ProceedingIdentifier}</> : <></>}} />); //TODO - verify if this should really be removed
				setProceedingIdentifier(sortedList[0].ace_ProceedingIdentifier);
			}
		});
	}

	function sortAsc(arr, field) {
		return arr.sort((a, b) => (a[field] > b[field] ? 1 : b[field] > a[field] ? -1 : 0));
	}

	function dateFormatter(date) {
		const smallMonth = (month) => month.length < 5 ? month : month.toLowerCase().startsWith('s') || month.toLowerCase().startsWith('ju') ? month.substring(0, 4) : month.substring(0, 3);
		try {
			const [year, month, day] = date.split('-');
			return `${year}-${smallMonth(month)}-${day}`;
		} catch {
			return date;
		}
	}

	function calculateTotalSizeInMB() {
		const totalBytes = Object.values(selectedRows).reduce((sum, row) => sum + parseInt(row.ace_FileSize, 10), 0);
		const totalKB = (totalBytes / 1000).toFixed(1);
		return (totalKB / 1000).toFixed(1);
	}

	function selectAll() {
		if (!isAllSelected) {
			setIsAllSelected(true);
			checkAll();
		} else {
			setIsAllSelected(false);
			uncheckAll();
		}
	}

	function checkAll() {
		const newSelected = {};
		exhibitList.forEach((row, index) => {
			if (row.ace_FileSize !== 0 && isLoggedIn) {
				newSelected["dLink-" + index] = row;
			}
		});
		setPrevSelectedRows(selectedRows);
		setSelectedRows(newSelected);
	}

	function uncheckAll() {
		setPrevSelectedRows(selectedRows);
		setSelectedRows({});
		setIsAllSelected(false);
	}

	function listLinks(row, index) {
		if (row.ace_FileSize !== 0 && isLoggedIn) {
			setPrevSelectedRows(selectedRows);
			setSelectedRows(prev => {
				const newSelected = { ...prev };
				if (newSelected["dLink-" + index]) {
					delete newSelected["dLink-" + index];
				} else {
					newSelected["dLink-" + index] = row;
				}
				return newSelected;
				// return prev;
			});
		}
	}

	function downloadLink(row, index) {
		const isSelected = !!selectedRows["dLink-" + index];
		const ace_name = row.ace_name;
		const ace_Filename = !!row.ace_Filename ? row.ace_Filename : "";
		const ace_FileSize = row.ace_FileSize;
		return (
		  <>
			{isLoggedIn && ace_FileSize !== 0 ?
				<span id={"dLink-" + index} style={{ visibility: "visible" }} className={`glyphicon ${isSelected ? 'glyphicon-check' : 'glyphicon-unchecked'}`}></span>
				:
				<span id={"dLink-" + index} style={{ visibility: "hidden" }} className="glyphicon glyphicon-unchecked"></span>
			}
			{' '}
			{!isLoggedIn ?
				ace_name || !!ace_Filename
				:
				ace_FileSize === 0 ?
					ace_name || !!ace_Filename
					:
					ace_name ?
					row?.ace_Filename.split('.').length === 1 ?
						<button className="link">{ace_name}<br />{'(' + bytesToNearestUnitStr(ace_FileSize, messages) + ')'}</button>
						:
						<button className="link">{ace_name}<br />{'(' + ace_Filename.split('.').pop().toUpperCase() + ','}<br />{bytesToNearestUnitStr(ace_FileSize, messages) + ')'}</button>
					:
					ace_Filename.split('.').length === 1 ?
						<button className="link">{ace_Filename}<br />{'(' + bytesToNearestUnitStr(ace_FileSize, messages) + ')'}</button>
						:
						<button className="link">{ace_Filename}<br />{'(' + ace_Filename.split('.').pop().toUpperCase() + ','}<br />{bytesToNearestUnitStr(ace_FileSize, messages) + ')'}</button>
			}
		  </>
		);
	}

	function downloadAllDocs() {
		const dLinkKeyList = Object.keys(selectedRows);
		if (dLinkKeyList.length > 0) {
			let attachments = dLinkKeyList.map(key => selectedRows[key]);
			groupAttachmentsThenLoad(proceedingIdentifier, attachments);
		}
	}

	async function auditDownload(idListForAudit) {
		let apiURL = "auditDownload";
		let apiParamsObj = { body: { "gcDocsFileIdList": idListForAudit } };
		try {
			await API.post("backend-api", apiURL, apiParamsObj);
		} catch (error) {
			if (window.env.environment === "local") {
				console.log('auditDownload error:', error);
			}
		}
	}

	function buttonFormattedMessage() {
		if (buttonRef.current) {
			const numFiles = Object.keys(selectedRows).length;
			const totalSizeMB = calculateTotalSizeInMB();
			const attachmentGroups = getAttachmentGroups(Object.values(selectedRows));
			const buttonMessage = intl.formatMessage(
				{ id: "ace.exhibits.click-here-to-download-selected-files" },
				{ number: numFiles, s: numFiles === 1 ? '' : 's', size: numFiles > 0 ? ` (${totalSizeMB} MB)` : '', es: numFiles === 1 ? '' : 'es', batchSize: attachmentGroups.length}
			);
			buttonRef.current.textContent = buttonMessage;
		}
	}

	useEffect(() => {
		const downloadButton = document.getElementById("download-button");
		if (downloadButton) {
			if (Object.keys(selectedRows).length === 0) {
				downloadButton.style.visibility = "hidden";
				downloadButton.style.display = "none";
			} else {
				buttonFormattedMessage();
				downloadButton.style.visibility = "visible";
				downloadButton.style.display = "block";
				if (Object.keys(prevSelectedRows).length === 0) {
					downloadButton.classList.add('animate', 'pop');
					setTimeout(() => downloadButton.classList.remove('animate', 'pop'), 250);
				}
			}
		}
	}, [selectedRows]);

	function ExhibitTableFooter() {
		return (
			<aside className="wb-fnote wb-init wb-fnote-inited" role="note" id="wb-auto-5">
				<h3 id="fn"><FormattedMessage id="ace.exhibit.footnote.footnotes" /></h3>
				<dl>
					<dt id="fn1-dt"><FormattedMessage id="ace.exhibit.footnote" values={{number: "1"}} /></dt>
					<dd id="fn1" tabIndex={-1}>
						<FormattedMessage id="ace.exhibit.footnote.1.title" values={{b: (chunks) => <b>{chunks}</b>, p: (chunks) => <p>{chunks}</p>}}/>
						<FormattedMessage id="ace.exhibit.footnote.1.title.1" values={{b: (chunks) => <b>{chunks}</b>, p: (chunks) => <p>{chunks}</p>}}/>
						<FormattedMessage id="ace.exhibit.footnote.1.explanation.1.1" values={{abbr: (chunks) => <abbr title={messages["ace.exhibit.footnote.SIMA.abbr"]}>{chunks}</abbr>, p: (chunks) => <p>{chunks}</p>}}/>
						<FormattedMessage id="ace.exhibit.footnote.1.explanation.1.2" values={{abbr: (chunks) => <abbr title={messages["ace.exhibit.footnote.CITT.abbr"]}>{chunks}</abbr>, p: (chunks) => <p>{chunks}</p>}}/>
						<FormattedMessage id="ace.exhibit.footnote.1.title.2" values={{b: (chunks) => <b>{chunks}</b>, p: (chunks) => <p>{chunks}</p>}}/>
						<FormattedMessage id="ace.exhibit.footnote.1.explanation.2.1" values={{p: (chunks) => <p>{chunks}</p>}}/>
						<p className="fn-rtn"><a href="#fn1-rf"><FormattedMessage id="ace.exhibit.footnote.return" values={{number: "1", span: (chunks) => <span className="wb-inv">{chunks}</span>}} /></a></p>
					</dd>
					<dt id="fn2-dt"><FormattedMessage id="ace.exhibit.footnote" values={{number: "2"}} /></dt>
					<dd id="fn2" tabIndex={-1}>
						<FormattedMessage id="ace.exhibit.footnote.2.title.1" values={{b: (chunks) => <b>{chunks}</b>, p: (chunks) => <p>{chunks}</p>}}/>
						<FormattedMessage id="ace.exhibit.footnote.2.list" values={{li: (chunks) => <li>{chunks}</li>, ul: (chunks) => <ul>{chunks}</ul>}} />
						<p className="fn-rtn"><a href="#fn2-rf"><FormattedMessage id="ace.exhibit.footnote.return" values={{number: "2", span: (chunks) => <span className="wb-inv">{chunks}</span>}} /></a></p>
					</dd>
				</dl>
			</aside>
		);
	}

	function getAttachmentGroups(attachments) {
		let attachmentGroups = [{
			totalSize: 0,
			attachments: []
		}];
		let attachmentGroupIndex = 0;
		for (let attachment of attachments) {
			if (attachmentGroups[attachmentGroupIndex].totalSize + attachment.ace_FileSize > batchSizeLimit) {
				attachmentGroupIndex++;
			}
			if (!attachmentGroups[attachmentGroupIndex]) {
				attachmentGroups[attachmentGroupIndex] = {
					totalSize: 0,
					attachments: []
				};
			}
			attachmentGroups[attachmentGroupIndex].attachments.push(attachment);
			attachmentGroups[attachmentGroupIndex].totalSize += attachment.ace_FileSize;
		}
		return attachmentGroups;
	}

	function groupAttachmentsThenLoad(proceedingId, attachments) {

		let attachmentGroups = getAttachmentGroups(attachments);
		downloadsInProgressRef.current += attachmentGroups.length;
		setShowSpinner(true);
		for (let i = 0; i < attachmentGroups.length; i++) {
			if (attachmentGroups[i].attachments.length > 0) {
				const timeout = i*5000;	//spread out the requests to 1/second so they don't seem like bot spamming
				setTimeout(() => loadAttachments(proceedingId, attachmentGroups[i].attachments, attachmentGroups.length), timeout);
			}
		}
	}

	function loadAttachments(proceedingId, attachments, numberOfGroups) {
		let downloadUrlKeys = {};
		let idListForAudit = [];
		attachments.forEach((attachment) => {
			downloadUrlKeys[attachment.ace_name] = true;
			idListForAudit.push(attachment.ace_DocumentId);
		});
		console.log(idListForAudit);
		let downloadUrlKey = "|";
		Object.keys(downloadUrlKeys).forEach((key) => {
			downloadUrlKey += key+"|"
		});

		const currentDateTime = (new Date()).getTime();
		if (!!downloadUrls[downloadUrlKey]?.url && (downloadUrls[downloadUrlKey]?.urlExpiry ?? currentDateTime) > currentDateTime) {
			//we already have a valid URL, don't bother grabbing it, just click it
			setTimeout(()=> {
				document.getElementById(downloadUrlKey+"-url").click();
				setPrevSelectedRows(selectedRows);
				setSelectedRows({});
				auditDownload(idListForAudit);
				downloadsInProgressRef.current = Math.max(downloadsInProgressRef.current - 1, 0);
				if (downloadsInProgressRef.current === 0) {
					setShowSpinner(false);
				}
			}, 500);
		} else {
			//don't yet have a valid URL, go get it
			let files = attachments.map(att => ({
				fileId: att.ace_name,
				fileName: att.ace_Filename,
				documentId: att.ace_DocumentId
			}));

			downloadUrls[downloadUrlKey] = { requestCount: 0, files };

			setDownloadUrls(prev => ({...prev}));
			getAttachmentUrls(proceedingId, downloadUrlKey, idListForAudit, numberOfGroups);
		}
	}

	function getAttachmentUrls(proceedingId, downloadUrlKey, idListForAudit, numberOfGroups) {
		if (downloadUrls[downloadUrlKey]?.requestCount > 120) {
			clearInterval(downloadUrls[downloadUrlKey].intervalId);
			appContext.setFlashNotice({ duration: -1, messages: [labels["home.details.fileProcessingIsBeingSlowCheckBackLater"]] });
			downloadsInProgressRef.current = Math.max(downloadsInProgressRef.current - 1, 0);
			if (downloadsInProgressRef.current === 0) {
				setShowSpinner(false);
			}
			return;
		}
		let apiURL = "exhibits";
		let documentIds = []
		downloadUrls[downloadUrlKey].files.forEach((file) => {
			documentIds.push(file.documentId);
		});
		let apiParamsObj = {
			queryStringParameters: { "proceedingId": proceedingId },
			body: { ace_documents: documentIds }
		};
		//Check to see if the file exists in the S3 bucket already and if so, get the pre-signed URL for that file
		appContext.setPauseTimeout(true);
		API.post("backend-api", apiURL, apiParamsObj).then(response => {
			// The file(s) exists
			if (!response.url) {
				let eta = 0;
				if (response.status==="QUEUING_FILES_FOR_DOWNLOAD") {
					for (let file of response.files) {
						eta = Math.max(eta, file.eta);
					}
				}
				throw new DownloadException("File not ready.", {status: response.status, eta: eta});	//invalid response, we likely got an error response because the file doesn't exist yet
			}

			const currentDateTime = (new Date()).getTime();
			downloadUrls[downloadUrlKey].url = response.url;
			downloadUrls[downloadUrlKey].urlExpiry = currentDateTime + 1800000;   //1800000 is the number of milliseconds in 30 minutes

			if (downloadUrls[downloadUrlKey].intervalId) {	//this getAttachmentUrl function might be called on an interval.  Since we have a success, clear the interval
				clearInterval(downloadUrls[downloadUrlKey].intervalId);
				delete downloadUrls[downloadUrlKey].intervalId;
			}
			downloadUrls[downloadUrlKey].requestCount = 0;
			downloadsInProgressRef.current = Math.max(downloadsInProgressRef.current - 1, 0);
			if (downloadsInProgressRef.current === 0) {
				setShowSpinner(false);
			}


			setDownloadUrls({...downloadUrls});

			setTimeout(() => {
				document.getElementById(downloadUrlKey+"-url").click();
				setPrevSelectedRows(selectedRows);
				setSelectedRows({});
				auditDownload(idListForAudit);
			}, 500);	//doing this on a timeout to make sure it's available on page for click
		}).catch(error => {

			// The file doesn't exist (yet?... yet... because A2A might still be in the process of trying to transfer it)
			if (window.env.environment === "local") {
				console.log({error});
			}

			if (error === "No current user") {
				downloadsInProgressRef.current = Math.max(downloadsInProgressRef.current - 1, 0);
				if (downloadsInProgressRef.current === 0) {
					setShowSpinner(false);
				}

				setDisplayPopup(true);
			} else if ((error?.response?.data?.Error?.Code ?? "") === "INTERNAL_ERROR" && (error?.response?.data?.Error?.Message?.search(/\|/) ?? -1) > -1) {
				//exhibits returned an error message from GCCase -- post this error
				let errorMessageArr = error.response.data.Error.Message.split("|");
				let errorMessage = document.documentElement.lang === "fr" ? errorMessageArr[1] : errorMessageArr[0];

				appContext.setFlashError({duration:10000, messages:[errorMessage]});
			} else if ((error?.response?.data?.Error?.Code ?? "") === "FORBIDDEN") {
				appContext.setFlashError({duration:10000, messages:[messages["error.oneOrMoreFilesRequestedIsNotAccessibleToCurrentUser"]]});
			} else {
				//what to do if the file is not there - YET
				if (downloadUrls[downloadUrlKey].requestCount === 0) {
					//only enter this block on the very first failure to receive the URL (otherwise we would be multiplying our recursive loop exponentially)

					//if this is the first request of this group of files, and the request for getAttachmentUrls failed, automatically request that the file(s) be made available for download

					let delayStart = 0;
					if ((error?.eta ?? 0) > 0) {
						delayStart = Math.max(error.eta - Date.now(), 0);
					}
					//then periodically check if the attachment url exists yet, delay checking for
					setTimeout(() => {downloadUrls[downloadUrlKey].intervalId = setInterval(() => {
						if (!downloadUrls[downloadUrlKey].url) {
							getAttachmentUrls(proceedingId, downloadUrlKey, idListForAudit);	//re-run the function to check for the URL recursively every 5000 ms
						} else if (downloadUrls[downloadUrlKey].intervalId) {
							//clearing the interval (stopping the loop) if we have an url properly received
							clearInterval(downloadUrls[downloadUrlKey].intervalId);
						}
					}, 10000*numberOfGroups)}, delayStart);
				}
				downloadUrls[downloadUrlKey].requestCount++;
			}
		}).finally(() => {
			if (downloadsInProgressRef.current <= 0) {
				appContext.setPauseTimeout(false);
			}
		});
	}

	return (
		<>
		  <MagnifiquePopup
			id="continueLoginConfirmation"
			trigger={displayPopup}
			setTrigger={setDisplayPopup}
			continueCallback={() => navigation.navigate("Login", currentLangParam())}
			cancelCallback={() => setCancelCallbackLatch(!cancelCallbackLatch)}
			header={<FormattedMessage id="message.login.confirmation" />}
			body={<FormattedMessage id="message.login.explanation" values={{ p: (chunks) => <p>{chunks}</p>, b: (chunks) => <b>{chunks}</b> }} />}
		  />
		  <Spinner show={showSpinner} />
		  <br />
		  {exhibitList.some(exhibit => exhibit.ace_FileSize !== 0) ?
				<button id="select-all-button" className="select-all-button" onClick={selectAll}>
					<span id="select-all" className={`glyphicon ${isAllSelected ? 'glyphicon-check' : 'glyphicon-unchecked'}`}></span>{' '}
					<FormattedMessage id={isAllSelected ? "ace.exhibit.table.subheader.3" : "ace.exhibit.table.subheader.2"} />
				</button>
				: null
		  }
		  <WetLikeTable sampleExhibitsFromGCCase={exhibitList} isLoggedIn={isLoggedIn} listLinks={listLinks} downloadAllDocs={downloadAllDocs} downloadUrls={downloadUrls} dateFormatter={dateFormatter} downloadLink={downloadLink} messages={messages} buttonRef={buttonRef} setIsAllSelected={setIsAllSelected} />
		  <ExhibitTableFooter />
		</>
	);
}

class DownloadException extends Error {
	constructor(message, infoObj, options) {
		super(message, options);

		this.message = message;
		Object.keys(infoObj).forEach((key) => {
			this[key] = infoObj[key];
		})
	}
}

Exhibits.path = "ex";
Exhibits.navigationOptions = {
	pageTitle: undefined,
	breadcrumb: [
		<PopupLink url={window.env["canada-url"][currentLang()]}><FormattedMessage id="breadcrumb.Canada.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.CBSA.text" /></PopupLink>
		, <PopupLink url={window.env["cbsa-sima-breadcrumbs-url"][currentLang()]}><FormattedMessage id="breadcrumb.ACE.text" /></PopupLink>
	],
	breadcrumbTitle: <FormattedMessage id="ace.exhibit.page.title" values={{ proceedingIdentifier: !!((new URLSearchParams(document.location.search)).get('proceedingId')) ? <><FormattedMessage id="wet.colon"/> {(new URLSearchParams(document.location.search)).get('proceedingId')}</> : <></> }} />
};

export default Exhibits;